import {
  CreateLtlePayload,
  TourOffer,
  TourOfferBookingTerms,
  TourOfferConfigurationContent,
  TourOfferConfigurationDetailsInput,
  TourOfferContent,
  TourOfferDeparture,
  TourOfferDepartureItinerary,
  TourOfferFormOptions,
  TourOfferHotelAddOns,
  TourOfferItineraryInput,
  TourOfferNewDepartures,
  TourOfferPaymentScheduleTemplate,
  TourOfferPricing,
  TourOfferPricingInput,
  TourOfferRelationshipsInput,
  TourOfferScheduleInput,
  TourOfferSummary,
  TourOfferTravellersInput,
} from '~/types/tours/tourOffer';

import { json_headers, request } from './common';
import { OkResponse, toursBasePath } from './toursApi';

export async function searchTourOffers(
  params: { search?: string; ltleOnly?: boolean },
  options?: { signal?: AbortSignal },
): Promise<Array<TourOfferSummary>> {
  const search = new URLSearchParams();
  if (params.search) {
    search.set('search', params.search);
  }
  if (params.ltleOnly) {
    search.set('ltleOnly', 'true');
  }
  const query = search.toString();

  const response: OkResponse<Array<TourOfferSummary>> = await request(
    `${toursBasePath()}/tour-offers${query ? `?${query}` : ''}`,
    { method: 'GET', signal: options?.signal },
  );

  return response.result;
}

export async function getTourOfferFormOptions(options?: { signal?: AbortSignal }): Promise<TourOfferFormOptions> {
  const response: OkResponse<TourOfferFormOptions> = await request(`${toursBasePath()}/tour-offers/form-options`, {
    method: 'GET',
    signal: options?.signal,
  });
  return response.result;
}

export async function getTourOfferDepartures(
  tourOfferId: string,
  options?: { signal?: AbortSignal },
): Promise<Array<TourOfferDeparture>> {
  const response: OkResponse<Array<TourOfferDeparture>> = await request(
    `${toursBasePath()}/tour-offers/${tourOfferId}/departures`,
    { method: 'GET', signal: options?.signal },
  );

  return response.result;
}

// By tour offer id, so a native-only LTLE (no legacy tour) can be read and edited.
function tourOfferPath(tourOfferId: string): string {
  return `${toursBasePath()}/tour-offers/${tourOfferId}`;
}

export async function getTourOffer(tourOfferId: string, options?: { signal?: AbortSignal }): Promise<TourOffer> {
  const response: OkResponse<TourOffer> = await request(tourOfferPath(tourOfferId), {
    method: 'GET',
    signal: options?.signal,
  });
  return response.result;
}

export async function updateTourOffer(
  tourOfferId: string,
  changes: {
    name?: string;
    status?: TourOffer['status'];
    brand?: string;
    emailPreHeader?: string | null;
    newDepartures?: TourOfferNewDepartures;
  },
): Promise<TourOffer> {
  const response: OkResponse<TourOffer> = await request(tourOfferPath(tourOfferId), {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(changes),
  });
  return response.result;
}

export async function putTourOfferContent(tourOfferId: string, content: TourOfferContent): Promise<TourOffer> {
  const response: OkResponse<TourOffer> = await request(`${tourOfferPath(tourOfferId)}/content`, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(content),
  });
  return response.result;
}

export async function putTourOfferBookingTerms(
  tourOfferId: string,
  bookingTerms: TourOfferBookingTerms,
): Promise<TourOffer> {
  const response: OkResponse<TourOffer> = await request(`${tourOfferPath(tourOfferId)}/booking-terms`, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(bookingTerms),
  });

  return response.result;
}

export async function putTourOfferConfigurationContent(
  tourOfferId: string,
  configurationId: string,
  content: TourOfferConfigurationContent,
): Promise<TourOffer> {
  const response: OkResponse<TourOffer> = await request(
    `${tourOfferPath(tourOfferId)}/configurations/${configurationId}/content`,
    { method: 'PUT', headers: json_headers, body: JSON.stringify(content) },
  );
  return response.result;
}

export async function getTourOfferPaymentScheduleTemplates(
  tourOfferId: string,
  supplierPaymentDueDays: number | null,
  options?: { signal?: AbortSignal },
): Promise<Array<TourOfferPaymentScheduleTemplate>> {
  const query = supplierPaymentDueDays === null ? '' : `?supplierPaymentDueDays=${supplierPaymentDueDays}`;
  const response: OkResponse<Array<TourOfferPaymentScheduleTemplate>> = await request(
    `${tourOfferPath(tourOfferId)}/payment-schedule-templates${query}`,
    { method: 'GET', signal: options?.signal },
  );
  return response.result;
}

export async function putTourOfferConfigurationDetails(
  tourOfferId: string,
  configurationId: string,
  details: TourOfferConfigurationDetailsInput,
): Promise<TourOffer> {
  const response: OkResponse<TourOffer> = await request(
    `${tourOfferPath(tourOfferId)}/configurations/${configurationId}/details`,
    { method: 'PUT', headers: json_headers, body: JSON.stringify(details) },
  );
  return response.result;
}

export async function putTourOfferConfigurationHotels(
  tourOfferId: string,
  configurationId: string,
  hotels: TourOfferHotelAddOns,
): Promise<TourOffer> {
  const response: OkResponse<TourOffer> = await request(
    `${tourOfferPath(tourOfferId)}/configurations/${configurationId}/hotels`,
    { method: 'PUT', headers: json_headers, body: JSON.stringify(hotels) },
  );
  return response.result;
}

export async function putTourOfferConfigurationItinerary(
  tourOfferId: string,
  configurationId: string,
  itinerary: TourOfferItineraryInput,
): Promise<TourOffer> {
  const response: OkResponse<TourOffer> = await request(
    `${tourOfferPath(tourOfferId)}/configurations/${configurationId}/itinerary`,
    { method: 'PUT', headers: json_headers, body: JSON.stringify(itinerary) },
  );
  return response.result;
}

export async function putTourOfferGallery(
  tourOfferId: string,
  images: Array<{ svcImageId: string; caption: string | null }>,
): Promise<TourOffer> {
  const response: OkResponse<TourOffer> = await request(`${tourOfferPath(tourOfferId)}/gallery`, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify({ images }),
  });
  return response.result;
}

export async function putTourOfferTravellers(
  tourOfferId: string,
  travellers: TourOfferTravellersInput,
): Promise<TourOffer> {
  const response: OkResponse<TourOffer> = await request(`${tourOfferPath(tourOfferId)}/travellers`, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(travellers),
  });
  return response.result;
}

export async function putTourOfferOptionalExperiences(
  tourOfferId: string,
  experiences: Array<{
    id: string;
    name: string;
    description: string | null;
    photos: Array<string>;
    isTransfer: 'arrival' | 'departure' | null;
  }>,
): Promise<TourOffer> {
  const response: OkResponse<TourOffer> = await request(`${tourOfferPath(tourOfferId)}/optional-experiences`, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify({ experiences }),
  });
  return response.result;
}

export async function copyTourOfferContent(tourOfferId: string, fromTourOfferId: string): Promise<TourOffer> {
  const response: OkResponse<TourOffer> = await request(`${tourOfferPath(tourOfferId)}/copy-content`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify({ fromTourOfferId }),
  });
  return response.result;
}

export async function getTourOfferDepartureItineraries(
  tourOfferId: string,
  configurationId: string,
  options?: { signal?: AbortSignal },
): Promise<Array<TourOfferDepartureItinerary>> {
  const response: OkResponse<Array<TourOfferDepartureItinerary>> = await request(
    `${tourOfferPath(tourOfferId)}/configurations/${configurationId}/departure-itineraries`,
    { method: 'GET', signal: options?.signal },
  );
  return response.result;
}

export async function putTourOfferDepartureItinerary(
  tourOfferId: string,
  configurationId: string,
  departureId: string,
  itinerary: TourOfferItineraryInput,
): Promise<Array<TourOfferDepartureItinerary>> {
  const response: OkResponse<Array<TourOfferDepartureItinerary>> = await request(
    `${tourOfferPath(tourOfferId)}/configurations/${configurationId}/departure-itineraries/${departureId}`,
    { method: 'PUT', headers: json_headers, body: JSON.stringify(itinerary) },
  );
  return response.result;
}

/** Puts this offer's saved deposit on every tour of its brand. */
export async function applyTourOfferDepositToBrand(tourOfferId: string): Promise<TourOffer> {
  const response: OkResponse<TourOffer> = await request(`${tourOfferPath(tourOfferId)}/booking-terms/apply-to-brand`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify({}),
  });
  return response.result;
}

export async function getTourOfferPricing(
  tourOfferId: string,
  options?: { signal?: AbortSignal },
): Promise<TourOfferPricing> {
  const response: OkResponse<TourOfferPricing> = await request(`${tourOfferPath(tourOfferId)}/pricing`, {
    method: 'GET',
    signal: options?.signal,
  });
  return response.result;
}

export async function putTourOfferPricing(
  tourOfferId: string,
  pricing: TourOfferPricingInput,
): Promise<TourOfferPricing> {
  const response: OkResponse<TourOfferPricing> = await request(`${tourOfferPath(tourOfferId)}/pricing`, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(pricing),
  });
  return response.result;
}

export async function putTourOfferSchedules(
  tourOfferId: string,
  schedules: Array<TourOfferScheduleInput>,
): Promise<TourOffer> {
  const response: OkResponse<TourOffer> = await request(`${tourOfferPath(tourOfferId)}/schedules`, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify({ schedules }),
  });
  return response.result;
}

export async function putTourOfferRelationships(
  tourOfferId: string,
  relationships: TourOfferRelationshipsInput,
): Promise<TourOffer> {
  const response: OkResponse<TourOffer> = await request(`${tourOfferPath(tourOfferId)}/relationships`, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(relationships),
  });
  return response.result;
}

/** Creates a native LTLE from a base tour offer; its service links back to the base. */
export async function createLtle(baseTourOfferId: string, payload: CreateLtlePayload): Promise<TourOfferSummary> {
  const response: OkResponse<TourOfferSummary> = await request(
    `${toursBasePath()}/tour-offers/${baseTourOfferId}/ltles`,
    {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify(payload),
    },
  );

  return response.result;
}
